import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Timer, Pause } from "lucide-react";

function fmtHMS(totalSec) {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

/**
 * Live workshop-timer badge visible in the header of every page.
 * - Polls /api/jobs/active-timer every 10 s.
 * - Ticks locally every second while state==='running'.
 * - Hidden when no timer is active or when already on the Workshop Mode screen.
 */
export default function GlobalTimerBadge() {
  const location = useLocation();
  const onWorkshop = location.pathname.startsWith("/werkstatt");
  const { data: job } = useQuery({
    queryKey: ["active-timer-job"],
    queryFn: async () => (await api.get("/jobs/active-timer")).data,
    refetchInterval: 10000,
    // don't spam when Workshop Mode has its own subscription
    enabled: !onWorkshop,
  });
  const [, setTick] = useState(0);

  const state = job?.timer_state;
  useEffect(() => {
    if (state !== "running") return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  if (onWorkshop || !job || (state !== "running" && state !== "paused")) return null;

  const liveExtra = state === "running" && job.timer_last_resumed_at
    ? Math.max(0, (Date.now() - new Date(job.timer_last_resumed_at).getTime()) / 1000)
    : 0;
  const totalSec = (job.total_work_seconds || 0) + liveExtra;

  const running = state === "running";
  const cls = running
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20"
    : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20";
  const dot = running ? "bg-emerald-500 animate-pulse" : "bg-amber-500";
  const Icon = running ? Timer : Pause;
  const label = job.title || job.customer_name || "Auftrag";

  return (
    <Link
      to={`/werkstatt?job=${job.id}`}
      data-testid="global-timer-badge"
      title={`${running ? "Timer läuft" : "Timer pausiert"} · ${label}`}
      className={`hidden md:inline-flex items-center gap-2 pl-2 pr-3 py-1 rounded-full border text-xs font-medium transition-colors ${cls}`}
    >
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      <Icon className="h-3.5 w-3.5" />
      <span className="font-mono tabular-nums" data-testid="global-timer-display">{fmtHMS(totalSec)}</span>
      <span className="text-muted-foreground truncate max-w-[8rem] hidden lg:inline">· {label}</span>
    </Link>
  );
}
